import { useState, useEffect } from 'react';
import { BuildingLogItem } from '../components/buildingLog/BuildingLog';
import { getBuildingLogs } from '../services/RegistryService';
import { useAppSelector } from '../../common/hooks';
import { selectSecurity } from '../../common/store/securitySlice';

interface BuildingLogsFilters {
  units?: string[];
  profiles?: string[];
}

interface UseBuildingLogsReturn {
  buildingLogs: BuildingLogItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  totalLoaded: number;
  hasMore: boolean;
  fetchLogs: (isLoadingMore?: boolean) => Promise<void>;
  loadMoreLogs: () => void;
  refresh: () => void;
  clearError: () => void;
}

export const useBuildingLogs = (filters: BuildingLogsFilters = {}): UseBuildingLogsReturn => {
  const [buildingLogs, setBuildingLogs] = useState<BuildingLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalLoaded, setTotalLoaded] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);

  const security = useAppSelector(selectSecurity).ui;
  const hasSubscription = security.tokens?.filter((item) => item.type === 'building-subscription').length || 0;

  const fetchLogs = async (isLoadingMore = false) => {
    try {
      setError(null);
      
      if (isLoadingMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      const offset = isLoadingMore ? currentOffset : 0;
      const logs = await getBuildingLogs(100, offset, filters.units, filters.profiles);

      // Pokud dostaneme méně než 100 záznamů, znamená to, že už nejsou další
      if (logs.length < 100) {
        setHasMore(false);
      }

      if (isLoadingMore) {
        setBuildingLogs(prevLogs => {
          // Vytvoříme Set existujících ID pro rychlé vyhledávání
          const existingIds = new Set(prevLogs.map(log => log.id));
          // Filtrujeme pouze nové logy, které ještě nemáme
          const newLogs = logs.filter(log => !existingIds.has(log.id));
          const updatedLogs = [...prevLogs, ...newLogs];
          setTotalLoaded(updatedLogs.length);
          return updatedLogs;
        });
        setCurrentOffset(prev => prev + logs.length);
      } else {
        setBuildingLogs(logs);
        setCurrentOffset(logs.length);
        setTotalLoaded(logs.length);
        setHasMore(logs.length === 100); // Reset hasMore pro nové načtení
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Chyba při načítání logů';
      console.error('Error fetching building logs:', error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreLogs = () => {
    if (hasMore && !isLoadingMore && hasSubscription > 0) {
      fetchLogs(true);
    }
  };

  const refresh = () => {
    setBuildingLogs([]);
    setTotalLoaded(0);
    setCurrentOffset(0);
    setHasMore(true);
    fetchLogs(false);
  };

  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    fetchLogs(false);
  }, [filters.units, filters.profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upravit hasMore pokud uživatel nemá předplatné
  useEffect(() => {
    if (hasSubscription === 0) {
      setHasMore(false);
    }
  }, [hasSubscription]);

  return {
    buildingLogs,
    isLoading,
    isLoadingMore,
    error,
    totalLoaded,
    hasMore,
    fetchLogs,
    loadMoreLogs,
    refresh,
    clearError
  };
};
